import React from 'react';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import SuccessGeneric from '../../../components/SuccessGeneric';
import SuccessLoyalty from '../../../components/SuccessLoyalty';
import Text from '../../../components/themed/Text';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {NestedRouteParams} from '../../../navigation/offerRoute';
import {useStores} from '../../../stores/use-stores';
import rewardsTheme from '../../../theme/rewardsTheme';
import {resolveMemberDisplayName, rewardsMarketingName} from '../../../utils/rewards';
import {prepareSignInThenHome, resetToMainScreen} from '../../rewards/resetToHome';

type NestedNavigation = {navigate: (route: AppRoute, params: NestedRouteParams) => void};

type LoyaltyState = 'loyaltyPending' | 'loyaltyCreated' | 'loyaltyNone';

const AccountSuccessScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_SUCCESS>) => {
    const {rewardsStore, userStore, shopSelectionStore} = useStores();

    /**
     * There are 3 potential outcomes when account (MFL) is created:
     * - zen account (loyalty) is also created.
     * - zen account creation is pending. the user has opted in but the zen service has not completed.
     * - zen account not created. user did not opt in.
     */
    const loyaltyState: LoyaltyState = rewardsStore.hasRewards
      ? 'loyaltyCreated'
      : userStore?.loyaltyPending
        ? 'loyaltyPending'
        : 'loyaltyNone';

    const goHome = () => {
      // DIS-1917: a pending offer return wins. Only with a store: without one the
      // offer screen's store gate could not have been passed, so the intent is
      // left to expire.
      const offerRoute = shopSelectionStore?.selectedStore?.id ? userStore.takeOfferReturn() : null;
      if (offerRoute) {
        (navigation as unknown as NestedNavigation).navigate(...offerRoute);
        return;
      }
      navigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {screen: AppRoute.HOME},
      });
    };

    // A logged-in user (DIS-1758 path) has nothing to sign in to, so the
    // loyalty CTA goes Home like the generic success screen.
    const loyaltyCta = userStore.isLoggedIn
      ? {title: 'START SHOPPING', onPress: goHome}
      : {
          // DIS-1859: same label as the logged-in CTA; the tap still opens
          // sign-in (DIS-1628: the user is not signed in yet), which lands on
          // Home. No store: Find Your Store first, then sign-in.
          title: 'START SHOPPING',
          onPress: () => {
            const hasSelectedStore = !!shopSelectionStore?.selectedStore?.id;
            prepareSignInThenHome(
              {userStore},
              hasSelectedStore,
              rewardsStore.suburb?.locality ?? userStore.billingAddress?.locality,
            );
            if (!hasSelectedStore) {
              resetToMainScreen(AppRoute.ACCOUNT_NOT_LOGGED_IN, false);
              return;
            }
            navigation.navigate(AppRoute.ROOT, {
              screen: AppRoute.MAIN,
              params: {screen: AppRoute.ACCOUNT_NOT_LOGGED_IN},
            });
          },
        };

    const greeting = userStore.firstName ? `Nice one, ${userStore.firstName}!` : 'Welcome!';

    return (
      <ThemeProvider theme={rewardsTheme}>
        {loyaltyState === 'loyaltyNone' && (
          <SuccessGeneric
            text={`Your account is ready to go! Start shopping online now or check out our latest promotions.`}
            onPressHome={goHome}
          />
        )}
        {loyaltyState === 'loyaltyCreated' && (
          <SuccessLoyalty
            // DIS-1765: the card is ready, so every user goes Home to shop.
            goToHome={goHome}
            ctaTitle="START SHOPPING"
            cardNumber={rewardsStore.cardNumber}
            fullName={resolveMemberDisplayName(
              {firstName: rewardsStore.firstName, lastName: rewardsStore.lastName},
              {firstName: userStore.firstName, lastName: userStore.lastName},
              'AccountSuccessScreen',
            )}
            inStoreCode={rewardsStore.shortcode}
            walletUrl={rewardsStore.walletUrl}
            memberSince={rewardsStore.memberSince}
            isStaff={rewardsStore.isStaffSignup}
            isLmgStaff={rewardsStore.isLmgStaff}
            title={`HERE’S YOUR ${rewardsMarketingName.toUpperCase()} CARD`}
            // A string, so SuccessLoyalty styles it per width (body2 narrow, P1 centred wide).
            text={`${greeting} You are now a part of our ${rewardsMarketingName} program! Show this barcode at checkout to collect awesome rewards!`}
          />
        )}
        {loyaltyState === 'loyaltyPending' && (
          <SuccessLoyalty
            goToHome={loyaltyCta.onPress}
            ctaTitle={loyaltyCta.title}
            memberSince={rewardsStore.memberSince}
            isStaff={rewardsStore.isStaffSignup}
            isLmgStaff={rewardsStore.isLmgStaff}
            pending
            title="YOUR ACCOUNT HAS BEEN CREATED"
            text={
              <>
                <Text variant="body2">
                  Welcome to your new account {userStore.firstName}! A confirmation has been sent to
                  your email. Check back soon in the Rewards tab to see your card ready to be used.
                </Text>
                <Text variant="heading3" style={{marginVertical: 16}}>
                  Your rewards access will be available within 24 hours
                </Text>
                <Text variant="body2">
                  Check the rewards tab in the app to see when it’s ready to go.
                </Text>
              </>
            }
          />
        )}
      </ThemeProvider>
    );
  },
);

export default AccountSuccessScreen;
